/**
 * A garage's name is also its address: /<name>/inventory. Names are lowercase letters, digits and
 * hyphens, so they read the same in the URL as in the app.
 */

export const GARAGE_NAME_MIN = 3;
export const GARAGE_NAME_MAX = 50;

/**
 * A garage name may not start with one of these, so no garage looks official or collides with the
 * app's own paths. It may contain them: "my-demo-garage" is fine, "demo-shop" is not. "bmw" also
 * covers "bmwm" and "bmw-motorrad", and the makes of every make package are reserved.
 */
export const RESERVED_GARAGE_PREFIXES = [
  "account",
  "admin",
  "api",
  "assets",
  "bimmertools",
  "bmw",
  "demo",
  "help",
  "mini",
  "official",
  "porsche",
  "root",
  "settings",
  "support",
  "system",
  "test",
  "www",
] as const;

const TRANSLITERATE: Record<string, string> = { ä: "ae", ö: "oe", ü: "ue", ß: "ss" };

/** "Müllers Garage 2" → "muellers-garage-2": what a typed name becomes as a garage name. */
export function normalizeGarageName(input: string): string {
  return (
    input
      .normalize("NFC")
      .toLowerCase()
      .replace(/[äöüß]/g, (c) => TRANSLITERATE[c] ?? c)
      // "é" → "e": split off accents, which the next steps drop.
      .normalize("NFKD")
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "")
  );
}

export type GarageNameProblem =
  { kind: "tooShort" } | { kind: "tooLong" } | { kind: "reserved"; word: string };

/** What is wrong with a (normalized) garage name, or null when it can be used. */
export function garageNameProblem(name: string): GarageNameProblem | null {
  if (name.length < GARAGE_NAME_MIN) return { kind: "tooShort" };
  if (name.length > GARAGE_NAME_MAX) return { kind: "tooLong" };
  const word = RESERVED_GARAGE_PREFIXES.find((prefix) => name.startsWith(prefix));
  return word ? { kind: "reserved", word } : null;
}
